import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Landmark, Smartphone, Apple, Square, Wallet, Banknote, FileCheck2 } from "lucide-react";
import { PAYMENT_METHODS, BUSINESS } from "@/data/site";
import { SECTION, BTN_PRIMARY, INPUT_CLS, API } from "./styles";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ICONS = {
  zelle: Landmark,
  cashapp: Smartphone,
  applepay: Apple,
  square: Square,
  venmo: Wallet,
  cash: Banknote,
  check: FileCheck2,
};

const initial = { name: "", email: "", amount: "", reference: "", note: "" };

export const Payments = () => {
  const [form, setForm] = useState(initial);
  const [method, setMethod] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!method) {
      toast.error("Please choose the payment method you used.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/payment-notices`, { ...form, method });
      toast.success("Thank you — your payment notice has been sent.");
      setForm(initial);
      setMethod("");
    } catch (err) {
      toast.error("Something went wrong. Please call us to confirm your payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="payments" data-testid="payments-section" className={`${SECTION} bg-[#F4F4F1]`}>
      <div className="grid lg:grid-cols-2 gap-14">
        <div>
          <p className="font-accent-script text-2xl md:text-3xl text-[#D4AF37] mb-3">Simple & Flexible</p>
          <h2 className="font-serif text-3xl md:text-5xl font-semibold text-[#0A1F3C] gold-underline"
            data-testid="payments-headline">
            MAKE A PAYMENT
          </h2>
          <p className="text-[#5A6A85] mt-6 mb-10 leading-relaxed">
            Pay toward your renovation project — initial deposits, progress payments, or final
            invoice balances — using whichever method suits you best. Tap a handle to copy it.
            For Square card payments, request an approved pay link and we'll send it to you.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10" data-testid="payment-methods-grid">
            {PAYMENT_METHODS.map((m) => {
              const Icon = ICONS[m.id];
              const isCheck = m.id === "check";
              const copyValue = (val) => {
                navigator.clipboard?.writeText(val);
                toast.success(`Copied ${val}`);
              };
              return (
                <div key={m.id} data-testid={`payment-method-${m.id}`}
                  className={`${isCheck ? "bg-[#0A1F3C] border-[#D4AF37]/40" : "bg-white border-[#E2E2DE] shadow-sm"} border hover:border-[#D4AF37] transition-colors duration-300 p-5 text-center`}>
                  <Icon className="h-7 w-7 text-[#D4AF37] mx-auto mb-3" />
                  <p className={`text-sm font-semibold ${isCheck ? "text-white" : "text-[#0A1F3C]"} tracking-wide`}>{m.name}</p>
                  <p className={`text-[11px] mt-1 ${isCheck ? "text-blue-200/70" : "text-[#5A6A85]"}`}>{m.note}</p>
                  {m.copy ? (
                    <button type="button" onClick={() => copyValue(m.copy)} data-testid={`payment-copy-${m.id}`}
                      className="mt-3 block w-full text-[11px] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37] hover:text-[#050505] transition-colors duration-300 px-2 py-1.5 break-all">
                      {m.detail}
                    </button>
                  ) : (
                    <p className="mt-3 text-[11px] text-[#5A6A85] leading-relaxed">{m.detail}</p>
                  )}
                  {m.detailLabel && <p className="mt-1 text-[10px] text-[#8A97AB]">{m.detailLabel}</p>}
                  {m.copy2 && (
                    <button type="button" onClick={() => copyValue(m.copy2)} data-testid={`payment-copy-${m.id}-alt`}
                      className="mt-1.5 block w-full text-[11px] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37] hover:text-[#050505] transition-colors duration-300 px-2 py-1.5">
                      {m.detail2?.replace("or ", "")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-[#5A6A85] leading-relaxed" data-testid="payments-help">
            Questions about a payment? Active projects:{" "}
            <a href={BUSINESS.phoneActiveHref} className="text-[#D4AF37] hover:underline">{BUSINESS.phoneActive}</a>
            {" "}• Administration:{" "}
            <a href={BUSINESS.phoneAdminHref} className="text-[#D4AF37] hover:underline">{BUSINESS.phoneAdmin}</a>
          </p>
        </div>

        <div className="bg-white border border-[#E2E2DE] shadow-sm p-8 md:p-10">
          <h3 className="font-serif text-2xl text-[#0A1F3C] mb-2">Sent a Payment?</h3>
          <p className="text-sm text-[#5A6A85] mb-8 leading-relaxed">
            Let us know and we'll match it to your project and confirm receipt.
          </p>
          <form onSubmit={submit} className="space-y-7" data-testid="payment-notice-form">
            <div className="grid md:grid-cols-2 gap-7">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Full Name *</label>
                <input required data-testid="payment-name-input" className={INPUT_CLS} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Email *</label>
                <input required type="email" data-testid="payment-email-input" className={INPUT_CLS} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-7">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-2">Method *</label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="payment-method-select"
                    className="bg-transparent border-0 border-b border-[#C9C9C4] rounded-none text-[#0A1F3C] px-2 focus:ring-0">
                    <SelectValue placeholder="How did you pay?" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E2DE] text-[#0A1F3C]">
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.id} value={m.name} data-testid={`payment-method-option-${m.id}`}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Amount *</label>
                <input required data-testid="payment-amount-input" className={INPUT_CLS} value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="$0.00" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Invoice / Project Reference</label>
              <input data-testid="payment-reference-input" className={INPUT_CLS} value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Invoice #1024 or project address" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Note</label>
              <textarea rows={3} data-testid="payment-note-input" className={`${INPUT_CLS} resize-none`}
                value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Deposit, progress payment, final balance..." />
            </div>
            <button type="submit" disabled={loading} data-testid="payment-notice-submit-btn"
              className={`${BTN_PRIMARY} disabled:opacity-60`}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Sending..." : "Notify Us of Payment"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
